import { Button, Grid, ScrollView, TextField } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { deviceActionLogsByDeviceID } from "graphql/queries";
import DateRangeInput from "components/utils/DateRangeInput";
import DateRangeForm from "components/utils/DateRangeForm";

const DeviceActionLogs = ({ deviceID, deviceName, visible, onClose }) => {
  const [deviceActionLogs, setDeviceActionLogs] = useState([]);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDate, setModalDate] = useState({
    visible: false,
  });
  const [filters, setFilters] = useState({
    userEmail: "",
    actionName: "",
    actionValue: "",
    createdAt: {
      start: "",
      end: "",
    },
  });

  const onNext = () => {
    fetchWithFilters(filters, paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchWithFilters(filters, previous, false);
  };

  const onChangeFilter = (e) => {
    const { name, value } = e.target;
    setFilters((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const onClearDate = (name) => {
    setFilters((prevSt) => ({
      ...prevSt,
      [name]: {
        start: "",
        end: "",
      },
    }));
  };

  const onSubmitDateRange = (name, start, end) => {
    setFilters((prevSt) => ({
      ...prevSt,
      [name]: {
        start,
        end,
      },
    }));
    setModalDate({ visible: false });
  };

  async function fetchDeviceActionLogs(operationObj, isNext) {
    try {
      const deviceActionLogsData = await API.graphql(
        graphqlOperation(deviceActionLogsByDeviceID, {
          ...operationObj,
          deviceID,
        })
      );
      const deviceActionLogsItems =
        deviceActionLogsData.data.deviceActionLogsByDeviceID.items;
      setDeviceActionLogs(deviceActionLogsItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext)
          previous = operationObj.nextToken
            ? [...previous, operationObj.nextToken]
            : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken:
            deviceActionLogsData.data.deviceActionLogsByDeviceID.nextToken,
        };
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  const fetchWithFilters = (filters, nextTokenParam, isNext = true) => {
    let operationObj = {
      deviceID,
    };

    if (filters.userEmail)
      operationObj = {
        ...operationObj,
        filter: {
          ...operationObj?.filter,
          userEmail: { contains: filters.userEmail },
        },
      };
    if (filters.actionName)
      operationObj = {
        ...operationObj,
        filter: {
          ...operationObj?.filter,
          actionName: { contains: filters.actionName },
        },
      };
    if (filters.actionValue)
      operationObj = {
        ...operationObj,
        filter: {
          ...operationObj?.filter,
          actionValue: { contains: filters.actionValue },
        },
      };
    if (filters.createdAt.start)
      operationObj = {
        ...operationObj,
        filter: {
          ...operationObj?.filter,
          createdAt: {
            ge: moment(filters.createdAt.start).startOf("day").toISOString(),
            le: moment(filters.createdAt.end).endOf("day").toISOString(),
          },
        },
      };
    if (!operationObj?.hasOwnProperty("filter"))
      operationObj = {
        ...operationObj,
        limit: 3,
      };
    if (nextTokenParam)
      operationObj = {
        ...operationObj,
        nextToken: nextTokenParam,
      };

    fetchDeviceActionLogs(operationObj, isNext);
  };

  useEffect(() => {
    fetchWithFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const tableColumns = [
    {
      key: "actionName",
      label: strings.table.headers.actionName,
      _style: { width: "25%" },
    },
    {
      key: "actionValue",
      label: strings.table.headers.actionValue,
      _style: { width: "25%" },
    },
    {
      key: "userEmail",
      label: strings.table.headers.userEmail,
      _style: { width: "25%" },
    },
    {
      key: "createdAt",
      label: strings.table.headers.createdAt,
      _style: { width: "25%" },
    },
  ];

  return (
    <CModal
      size="xl"
      alignment="center"
      visible={visible}
      onClose={onClose}
      backdrop="static"
    >
      <CModalHeader>
        <CModalTitle>{strings.title} - {deviceName}</CModalTitle>
      </CModalHeader>
      <CModalBody style={{ overflow: "hidden" }}>
        <ScrollView maxHeight="75vh">
          <div>
            <div className="p-3">
              <Grid
                className="mb-3"
                gap={20}
                templateColumns={{ medium: "1fr 1fr" }}
              >
                <TextField
                  name="userEmail"
                  label={strings.filters.userEmail}
                  value={filters.userEmail}
                  onChange={onChangeFilter}
                ></TextField>
                <DateRangeInput
                  name="createdAt"
                  label={strings.filters.createdAt}
                  start={filters.createdAt.start}
                  end={filters.createdAt.end}
                  onClick={() => setModalDate({ visible: true })}
                  onClear={() => onClearDate("createdAt")}
                />
                <TextField
                  name="actionName"
                  label={strings.filters.actionName}
                  value={filters.actionName}
                  onChange={onChangeFilter}
                ></TextField>
                <TextField
                  name="actionValue"
                  label={strings.filters.actionValue}
                  value={filters.actionValue}
                  onChange={onChangeFilter}
                ></TextField>
              </Grid>
              <CTable
                className="table-centered"
                items={deviceActionLogs.map((it) => ({
                  ...it,
                  createdAt: moment(it.createdAt).format("YYYY-MM-DD HH:mm:ss"),
                }))}
                columns={tableColumns}
                tableHeadProps={{ color: "light" }}
                responsive
                hover
              />
              <Button
                size="small"
                disabled={paginationTokens.previous?.length <= 0}
                style={styles.buttonPrevious}
                onClick={onPrevious}
              >
                <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
              </Button>
              <Button
                size="small"
                disabled={!paginationTokens.nextToken}
                style={styles.buttonNext}
                onClick={onNext}
              >
                <FiArrowRight /> {strings.table.pagination.buttonNext}
              </Button>
            </div>
            <CModal
              alignment="center"
              visible={modalDate.visible}
              onClose={() => setModalDate({ visible: false })}
              portal={false}
              backdrop="static"
            >
              <CModalHeader>
                <CModalTitle>{strings.selectDateRange}</CModalTitle>
              </CModalHeader>
              <CModalBody>
                <DateRangeForm
                  onSubmit={({ from, to }) =>
                    onSubmitDateRange("createdAt", from, to)
                  }
                />
              </CModalBody>
            </CModal>
          </div>
        </ScrollView>
      </CModalBody>
    </CModal>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Historial de acciones",
    responses: {
      errorFetch: "Error al consultar historial de acciones",
    },
    filters: {
      userEmail: "Correo de usuario",
      actionName: "Nombre de acción",
      actionValue: "Valor de acción",
      createdAt: "Fecha de creación",
    },
    table: {
      headers: {
        userEmail: "Correo de usuario",
        createdAt: "Fecha de creación",
        actionName: "Nombre de acción",
        actionValue: "Valor de acción",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
    },
    selectDateRange: "Seleccionar Rango de Fecha",
  },
});

const styles = {
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
};

export default DeviceActionLogs;
